// src/components/devices/DeviceCard.js
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaLightbulb, FaCogs, FaToggleOn, FaThermometerHalf,
  FaVolumeUp, FaDoorClosed, FaQuestion, FaEdit, FaEye, FaEyeSlash,
  FaWindowRestore, FaStop, FaArrowUp, FaArrowDown, FaExternalLinkAlt
} from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import {
  DeviceCard as DSDeviceCard,
  DeviceHeader,
  Button
} from '../../design-system/components';
import DeviceEditor from './DeviceEditor';
import './DeviceCard.css';

const DeviceCard = ({ deviceId, device }) => {
  const { controlDevice } = useContext(AppContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleControlChange = async (property, value) => {
    try {
      setLoading(true);
      await controlDevice(device.id, property, value, device.parentId);
    } catch (error) {
      console.error(`Error setting ${property}:`, error);
      toast.error(`Failed to update ${device.name}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const toggleEditing = (e) => {
    e.stopPropagation();
    setIsEditing(!isEditing);
  };

  const renderThermostatControls = () => {
    const temp = device.sensors.find(c => 
      c.property.includes('local_temperature') || 
      c.property.includes('device_temperature')
    );
    
    const setpoint = device.controls.find(c => 
      c.property.includes('current_heating_setpoint')
    );
    const systemMode = device.controls.find(c => 
      c.property.includes('system_mode')
    );
    const fanMode = device.controls.find(c => 
      c.property.includes('fan_mode')
    );
    const power = device.controls.find(c => c.valueType === 'binary');
    
    if (!temp) return null;
    
    const isOn = power ? (power.value === true || power.value === 'ON' || power.value === power.options?.on) : true;
    
    return (
      <div className="px-4 pb-2 space-y-3">
        {/* Temperature Display */}
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="text-sm text-gray-600">Current Temp</span>
            <span className="text-2xl font-bold text-gray-900">{temp.value}°C</span>
          </div>
          {setpoint && (
            <div className="flex flex-col items-end">
              <span className="text-sm text-gray-600">Target</span>
              <span className="text-xl font-semibold text-blue-600">{setpoint.value}°C</span>
            </div>
          )}
        </div>

        {/* System Mode */}
        {systemMode && systemMode.options?.values && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Mode</span>
            <div className="flex gap-1 flex-wrap">
              {systemMode.options.values.map((mode) => (
                <Button
                  key={mode}
                  variant={systemMode.value === mode ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => handleControlChange(systemMode.property, mode)}
                  disabled={loading || !isOn}
                  className="px-2 py-1 text-xs capitalize"
                >
                  {mode.replace('_', ' ')}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Fan Mode */}
        {fanMode && fanMode.options?.values && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Fan</span>
            <div className="flex gap-1 flex-wrap">
              {fanMode.options.values.map((fan) => (
                <Button
                  key={fan}
                  variant={fanMode.value === fan ? 'success' : 'secondary'}
                  size="sm"
                  onClick={() => handleControlChange(fanMode.property, fan)}
                  disabled={loading || !isOn}
                  className="px-2 py-1 text-xs capitalize"
                >
                  {fan}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Setpoint Controls */}
        {setpoint && isOn && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Adjust Target</span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleControlChange(setpoint.property, Math.max(setpoint.value_min || 5, setpoint.value - 1))}
                disabled={loading || setpoint.value <= (setpoint.value_min || 5)}
                className="px-2 py-1"
              >
                -
              </Button>
              <span className="text-sm font-medium min-w-[3rem] text-center">
                {setpoint.value}°C
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleControlChange(setpoint.property, Math.min(setpoint.value_max || 35, setpoint.value + 1))}
                disabled={loading || setpoint.value >= (setpoint.value_max || 35)}
                className="px-2 py-1"
              >
                +
              </Button>
            </div>
          </div>
        )}

        {/* Power Control */}
        {power && (
          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
            <span className="text-sm text-gray-600">Power</span>
            <Button
              variant={isOn ? 'danger' : 'success'}
              size="sm"
              onClick={() => handleControlChange(power.property, isOn ? power.options?.off : power.options?.on)}
              disabled={loading}
            >
              {isOn ? 'OFF' : 'ON'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderPrimaryControls = () => {
    if (!device.controls || device.controls.length === 0) return null;

    const type = device.type?.toLowerCase();

    if (type === 'switch' || type === 'light') {
      const binaryControl = device.controls.find(c => c.valueType === 'binary');
      if (!binaryControl) return null;
      const isOn = binaryControl.value === true || binaryControl.value === 'ON' || binaryControl.value === binaryControl.options?.on;
      const toggleValue = isOn ? binaryControl.options?.off || false : binaryControl.options?.on || true;

      return (
        <div className="flex justify-between items-center px-4 pb-2">
          <span className="text-sm text-gray-600">Power</span>
          <label className="relative inline-block w-12 h-6">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isOn}
              onChange={() => handleControlChange(binaryControl.property, toggleValue)}
              disabled={loading}
            />
            <span className="block w-12 h-6 bg-gray-300 rounded-full shadow-inner cursor-pointer transition-colors duration-300 peer-checked:bg-green-500"></span>
            <span className="absolute left-0 top-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-300 peer-checked:translate-x-6"></span>
          </label>
        </div>
      );
    }

    if (type === 'thermostat') {
      return renderThermostatControls();
    }

    const curtainControl = device.controls?.find(c => 
      c.property === 'state' && 
      c.type === 'enum' && 
      Array.isArray(c.options?.values) &&
      c.options?.values?.includes('OPEN') && 
      c.options?.values?.includes('CLOSE')
    );
    
    if (curtainControl) {
      const positionControl = device.controls.find(c => c.property === 'position');
      const currentState = curtainControl.value;
      const currentPosition = positionControl ? positionControl.value : null;

      return (
        <div className="px-4 pb-2 space-y-3">
          {/* Status Display */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Status</span>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                currentState === 'OPEN' ? 'bg-green-100 text-green-700' :
                currentState === 'CLOSE' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {currentState === 'OPEN' ? 'Open' :
                 currentState === 'CLOSE' ? 'Closed' :
                 'Stopped'}
              </span>
              {currentPosition !== null && (
                <span className="text-sm text-gray-600">{currentPosition}%</span>
              )}
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex gap-2">
            <Button
              variant={currentState === 'OPEN' ? 'success' : 'secondary'}
              size="sm"
              onClick={() => handleControlChange(curtainControl.property, 'OPEN')}
              disabled={loading || currentState === 'OPEN'}
              className="flex-1"
              leftIcon={<FaArrowUp />}
            >
              Open
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => handleControlChange(curtainControl.property, 'STOP')}
              disabled={loading}
              className="px-3"
              leftIcon={<FaStop />}
            >
              Stop
            </Button>
            <Button
              variant={currentState === 'CLOSE' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => handleControlChange(curtainControl.property, 'CLOSE')}
              disabled={loading || currentState === 'CLOSE'}
              className="flex-1"
              leftIcon={<FaArrowDown />}
            >
              Close
            </Button>
          </div>
        </div>
      );
    }

    if (type === 'sensor') {
      const presenceSensor = device.sensors?.find(s => s.property === 'presence');
      const luxSensor = device.sensors?.find(s => s.property === 'illuminance');
      const distanceSensor = device.sensors?.find(s => s.property === 'target_distance');
      const presence = presenceSensor?.value === true;
    
      return (
        <div className="flex justify-between items-center px-4 pb-2 w-full">
          <div className="flex flex-col items-start">
            <span className="text-sm text-gray-600">Presence</span>
            <span className="text-lg">
              {presence ? (
                <FaEye className="text-green-600" />
              ) : (
                <FaEyeSlash className="text-gray-400" />
              )}
            </span>
          </div>
    
          <div className="flex flex-col items-end text-sm text-gray-600 text-right">
            {luxSensor && (
              <span>
                Lux: <span className="font-medium text-gray-900">{luxSensor.value}</span>
              </span>
            )}
            {distanceSensor && (
              <span>
                Distance: <span className="font-medium text-gray-900">{distanceSensor.value} m</span>
              </span>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderDeviceIcon = () => {
    // Check for curtain/cover controls based on exposed properties
    const hasCurtainControl = device.controls?.find(c => 
      c.property === 'state' && 
      c.type === 'enum' && 
      Array.isArray(c.options?.values) &&
      c.options.values.includes('OPEN') && 
      c.options.values.includes('CLOSE')
    );
    if (hasCurtainControl) {
      return <FaWindowRestore className="device-icon-svg" />;
    }

    if (device.type) {
      switch (device.type.toLowerCase()) {
        case 'light': return <FaLightbulb className="device-icon-svg" />;
        case 'switch': return <FaToggleOn className="device-icon-svg" />;
        case 'curtain': return <FaWindowRestore className="device-icon-svg" />;
        case 'thermostat': return <FaThermometerHalf className="device-icon-svg" />;
        case 'speaker': return <FaVolumeUp className="device-icon-svg" />;
        case 'sensor': return <FaCogs className="device-icon-svg" />;
      }
    }
    return <FaQuestion className="device-icon-svg" />;
  };

  const renderExpandedControls = () => {
    if (!device.controls || device.controls.length === 0) return null;
    return (
      <div className="space-y-4 pt-2">
        {device.controls.map((c) => (
          <div key={c.property} className="text-sm text-gray-600 flex justify-between">
            <span>{c.name}</span>
            <span className="font-semibold text-gray-800">{String(c.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <DSDeviceCard
      variant="interactive"
      isActive={true}
      className={`transition-all duration-300 ${isExpanded ? 'shadow-lg' : ''}`}
    >
      <DeviceHeader
        icon={renderDeviceIcon()}
        title={device.name || device.friendly_name || 'Unnamed Device'}
        subtitle={
          device.floor && device.location 
            ? `${device.location} - ${device.floor}`
            : device.location || 'Unknown Location'
        }
        status={'online'}
        onClick={toggleExpanded}
        actions={[<FaEdit key="edit" className="text-gray-600 hover:text-blue-600 cursor-pointer text-lg" onClick={toggleEditing} />]}
      />

      {renderPrimaryControls()}

      {isEditing && (
        <div className="border-t border-gray-200 p-4">
          <DeviceEditor device={device} onClose={() => setIsEditing(false)} />
        </div>
      )}

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-4 max-h-96 overflow-y-auto">
          {renderExpandedControls()}
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </DSDeviceCard>
  );
};

export default DeviceCard;